import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PeopleIcon from '@material-ui/icons/People';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import BuildIcon from '@material-ui/icons/Build';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    maxWidth: 600,
    margin: '0 auto',
    textAlign: 'center',
  },
  successIcon: {
    fontSize: 72,
    color: '#4caf50',
    marginBottom: theme.spacing(2),
  },
  summaryCard: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  summaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  linksSection: {
    width: '100%',
    textAlign: 'left',
    marginBottom: theme.spacing(4),
  },
  linkItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  provisioningSection: {
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    textAlign: 'left',
  },
  toolChip: {
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  cta: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    fontSize: '1rem',
  },
}));

interface ProvisionResult {
  status: 'created' | 'exists' | 'error';
  username: string;
  error?: string;
}

interface ToolProvisionResult {
  status: 'created' | 'exists' | 'error';
  error?: string;
}

interface TeamProvisionResponse {
  gitea: ToolProvisionResult;
  argocd: ToolProvisionResult;
  annotationPatched: boolean;
  provisioned: boolean;
}

interface Props {
  teamSlug?: string;
  onComplete: () => Promise<void>;
  onDone: () => void;
  onProvisionTeam?: (slug: string) => Promise<TeamProvisionResponse>;
}

export function CompleteStep({ teamSlug, onComplete, onDone, onProvisionTeam }: Props) {
  const classes = useStyles();
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [giteaResult, setGiteaResult] = useState<ProvisionResult | undefined>();

  // Team provisioning state
  const [provisioningTeam, setProvisioningTeam] = useState(false);
  const [teamProvisionResult, setTeamProvisionResult] = useState<TeamProvisionResponse | undefined>();
  const [teamProvisionError, setTeamProvisionError] = useState<string | undefined>();

  async function handleProvisionTeam() {
    if (!teamSlug || !onProvisionTeam) return;
    setProvisioningTeam(true);
    setTeamProvisionError(undefined);
    setTeamProvisionResult(undefined);
    try {
      const result = await onProvisionTeam(teamSlug);
      setTeamProvisionResult(result);
    } catch (err: any) {
      setTeamProvisionError(err.message ?? 'Team provisioning failed. Please try again.');
    } finally {
      setProvisioningTeam(false);
    }
  }

  async function handleGoToCatalog() {
    // onDone is called after completion to dismiss the wizard
    setCompleting(true);
    setError(undefined);
    try {
      // completeOnboarding calls provision-tools then marks complete
      await onComplete();
      // Optimistically show the Gitea result based on what the backend will have sent.
      // The actual provisioning result is visible via the annotation on the user entity.
      setGiteaResult({ status: 'created', username: 'provisioned' });
      setDone(true);
      // Dismiss the wizard and navigate — parent (OnboardingGate) handles routing
      onDone();
    } catch (err: any) {
      setError(err.message ?? 'Something went wrong. Please try again.');
    } finally {
      setCompleting(false);
    }
  }

  return (
    <Box className={classes.root}>
      <CheckCircleIcon className={classes.successIcon} />

      <Typography variant="h5" gutterBottom>
        You're all set!
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        You've completed the onboarding wizard. Click below to start using the
        platform — your accounts are being provisioned now.
      </Typography>

      <Box className={classes.summaryCard}>
        <Typography variant="subtitle2" gutterBottom>
          Summary
        </Typography>
        <Divider style={{ marginBottom: 12 }} />

        {teamSlug && (
          <Box className={classes.summaryRow}>
            <PeopleIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Team: <strong>{teamSlug}</strong>
            </Typography>
          </Box>
        )}

        <Box className={classes.summaryRow}>
          <LibraryBooksIcon fontSize="small" color="action" />
          <Typography variant="body2">Platform docs acknowledged ✓</Typography>
        </Box>
      </Box>

      {/* Team platform resource provisioning */}
      <Box className={classes.provisioningSection}>
        <Typography variant="subtitle2" gutterBottom>
          <PeopleIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Team platform resources
        </Typography>

        {!teamSlug && (
          <Typography variant="body2" color="textSecondary">
            Join or create a team first to provision team resources.
          </Typography>
        )}

        {teamSlug && !teamProvisionResult && !provisioningTeam && (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
              Provision a Gitea organisation and ArgoCD project for{' '}
              <strong>{teamSlug}</strong>.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<BuildIcon />}
              onClick={handleProvisionTeam}
              disabled={!onProvisionTeam}
            >
              Provision platform tools for your team
            </Button>
          </>
        )}

        {provisioningTeam && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Provisioning team resources…
            </Typography>
          </Box>
        )}

        {teamProvisionResult && (
          <Box style={{ marginTop: 8 }}>
            {teamProvisionResult.gitea.status !== 'error' ? (
              <Chip
                icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
                label="Gitea ✓"
                size="small"
                className={classes.toolChip}
              />
            ) : (
              <Chip
                icon={<ErrorIcon color="error" />}
                label={`Gitea: ${teamProvisionResult.gitea.error ?? 'failed'}`}
                size="small"
                className={classes.toolChip}
                color="secondary"
              />
            )}
            {teamProvisionResult.argocd.status !== 'error' ? (
              <Chip
                icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
                label="ArgoCD ✓"
                size="small"
                className={classes.toolChip}
              />
            ) : (
              <Chip
                icon={<ErrorIcon color="error" />}
                label={`ArgoCD: ${teamProvisionResult.argocd.error ?? 'failed'}`}
                size="small"
                className={classes.toolChip}
                color="secondary"
              />
            )}
          </Box>
        )}

        {teamProvisionError && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            {teamProvisionError}
          </Typography>
        )}
      </Box>

      {/* Tool provisioning status */}
      {completing && (
        <Box className={classes.provisioningSection}>
          <Typography variant="subtitle2" gutterBottom>
            <BuildIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Provisioning tool accounts…
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Setting up your Gitea account
            </Typography>
          </Box>
        </Box>
      )}

      {done && giteaResult && (
        <Box className={classes.provisioningSection}>
          <Typography variant="subtitle2" gutterBottom>
            <BuildIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
            Tool accounts ready
          </Typography>
          {giteaResult.status !== 'error' ? (
            <Chip
              icon={<CheckCircleIcon style={{ color: '#4caf50' }} />}
              label="Gitea ✓"
              size="small"
              className={classes.toolChip}
            />
          ) : (
            <Chip
              icon={<ErrorIcon color="error" />}
              label={`Gitea: ${giteaResult.error}`}
              size="small"
              className={classes.toolChip}
              color="secondary"
            />
          )}
        </Box>
      )}

      {/* Quick links */}
      <Box className={classes.linksSection}>
        <Typography variant="subtitle2" gutterBottom>
          Get started
        </Typography>
        <List dense disablePadding>
          <ListItem className={classes.linkItem} component="a" href="/create">
            <ListItemText primary="Create a new service from a template" />
          </ListItem>
          <ListItem className={classes.linkItem} component="a" href="/docs">
            <ListItemText primary="Browse TechDocs" />
          </ListItem>
          <ListItem
            className={classes.linkItem}
            component="a"
            href="/catalog?filters[kind]=group"
          >
            <ListItemText primary="View all teams in the catalog" />
          </ListItem>
        </List>
      </Box>

      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error}
        </Typography>
      )}

      {done ? (
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.cta}
          onClick={onDone}
        >
          Go to Home
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.cta}
          disabled={completing}
          onClick={handleGoToCatalog}
          startIcon={completing ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {completing ? 'Setting up…' : 'Complete & Go to Home'}
        </Button>
      )}
    </Box>
  );
}
